import { useEffect } from 'react'
import { useVersion } from '@/hooks/use-version'
import { setEnvTitlePrefix } from '@/lib/environment-store'

/**
 * Which environment the app is running in, for showing a badge on anything that is not
 * production.
 *
 *
 * IT COMES FROM THE SERVER, AND IT HAS TO
 *
 * There is no build-time signal to read. The frontend is built once, in a Docker stage that
 * receives no build args and no `.env`, and the same image is deployed to dev and prod. So
 * `import.meta.env.PROD` is `true` and `MODE` is `"production"` on **dev** as well — the
 * obvious implementation hides the badge exactly where it is needed. `NODE_ENV` is
 * `production` on both for the same reason.
 *
 * The server answers from Cloud Run's own `K_SERVICE` (`backend/src/utils/envLabel.js`),
 * which is authoritative rather than inferred. That also rules out reading
 * `window.location.hostname`: production serves canary revisions from
 * `canary---grm-prod-*.a.run.app` during a rollout, so a hostname rule would put a DEV badge
 * on real production traffic for the whole of every deploy, and an indicator that cries wolf
 * is worse than none.
 *
 *
 * NOTHING IS SHOWN UNTIL IT IS KNOWN — BUT A FAILURE IS NOT SILENCE
 *
 * `useVersion` caches for the session (`staleTime: Infinity`), so this is one request. While
 * it is in flight `label` is null and nothing renders, which is right: a badge that flashes
 * before settling trains people to ignore it.
 *
 * A FAILED request is different, and getting this wrong was a real defect. It used to fall
 * into the same branch as "still loading", so one transient failure left dev with no badge
 * and an unprefixed tab for the whole session — indistinguishable from production, which is
 * the exact mistake this indicator exists to prevent. The query now retries, and a
 * definitive failure reports `UNKNOWN` rather than nothing, on the same principle chosen for
 * an unrecognised environment: anything not known to be production is labelled, so an empty
 * header means production and never "we could not tell".
 */
export interface Environment {
  /** 'LOCAL', 'DEV', or an unrecognised service name upper-cased. Null on production. */
  label: string | null
  /** True only when the server said `prod`. Not "we could not tell". */
  isProduction: boolean
  /** A prefix for the browser tab, e.g. '[DEV] '. Empty on production and while unknown. */
  titlePrefix: string
}

export function useEnvironment(): Environment {
  const { data, isError } = useVersion()
  const envName = data?.envName

  const isProduction = envName === 'prod'
  const label = isProduction
    ? null
    : envName
      ? envName.toUpperCase()
      : // Only once the query has definitively given up. Still loading stays silent.
        isError
        ? 'UNKNOWN'
        : null
  const titlePrefix = label ? `[${label}] ` : ''

  // Published for useDocumentTitle, which cannot subscribe to a query of its own without
  // dragging a QueryClientProvider into every page test. This hook is called once, by the
  // header badge, so this is the single place the answer enters the app.
  useEffect(() => {
    setEnvTitlePrefix(titlePrefix)
  }, [titlePrefix])

  return { label, isProduction, titlePrefix }
}
